/**
 * Viewer-local tile order for the Team Live multiview: an ordered array of
 * namespaced tile keys (`member:{liveStreamId}` / `device:{deviceId}` /
 * `ext:{externalId}`) persisted per team in localStorage. Nothing is shared
 * between viewers or devices. See
 * docs/superpowers/specs/2026-08-24-team-live-tile-reorder-design.md
 */

export function memberTileKey(liveStreamId: string): string {
  return `member:${liveStreamId}`;
}

export function deviceTileKey(deviceId: string): string {
  return `device:${deviceId}`;
}

export function externalTileKey(externalId: string): string {
  return `ext:${externalId}`;
}

const storageKey = (teamId: string) => `teamLive:tileOrder:${teamId}`;

/** Corrupt or unknown-shape storage is treated as empty; never throws. */
export function loadTeamLiveTileOrder(teamId: string): string[] {
  try {
    const raw = window.localStorage.getItem(storageKey(teamId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((k): k is string => typeof k === "string");
  } catch {
    return [];
  }
}

/** Best-effort persist; quota/private-mode failures are swallowed. */
export function saveTeamLiveTileOrder(teamId: string, order: string[]): void {
  try {
    window.localStorage.setItem(storageKey(teamId), JSON.stringify(order));
  } catch {
    // Persistence failure only; the in-memory order still applies.
  }
}

/**
 * Pure ordering function: stored keys first (in stored order, deduplicated,
 * dropping keys no longer present), then current keys not in storage (in the
 * order given, deduplicated — server order). Deterministic, so polling
 * refetches of identical data produce identical output.
 */
export function mergeTeamLiveTileOrder(
  stored: string[],
  currentKeys: string[],
): string[] {
  const current = new Set(currentKeys);
  const seen = new Set<string>();
  const kept: string[] = [];
  for (const key of stored) {
    if (current.has(key) && !seen.has(key)) {
      seen.add(key);
      kept.push(key);
    }
  }
  for (const key of currentKeys) {
    if (!seen.has(key)) {
      seen.add(key);
      kept.push(key);
    }
  }
  return kept;
}

/**
 * Pure reorder for the mobile edit mode (2026-09-25 mobile tile layout
 * spec): swap `key` with its neighbour in `direction` (-1 = toward the
 * front, 1 = toward the back). Returns the same array reference (no-op) at
 * either boundary or when the key is absent, so callers can fire freely.
 */
export function moveTileKey(
  order: string[],
  key: string,
  direction: -1 | 1,
): string[] {
  const index = order.indexOf(key);
  const target = index + direction;
  if (index === -1 || target < 0 || target >= order.length) return order;
  const next = [...order];
  next[index] = order[target];
  next[target] = key;
  return next;
}
